import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  IconButton,
  Button,
  Switch,
  FormControl
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { notificationService } from '../../Services/NotificationService';
import '../../UI/Common.css';
import './Notifications.css';

const emptyForm = { event: '', role: '', message: '' };

export default function NotificationSettings() {
  const laboratoryId = sessionStorage.getItem('LabID');

  const [metadata, setMetadata] = useState({ events: [], roles: [] });
  const [isEnabled, setIsEnabled] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    if (!laboratoryId) return;
    try {
      setLoading(true);
      const [metaData, setting, configList] = await Promise.all([
        notificationService.getMetadata(),
        notificationService.getSetting(laboratoryId),
        notificationService.getConfigs(laboratoryId)
      ]);
      setMetadata(metaData);
      setIsEnabled(setting?.isEnabled ?? true);
      setConfigs(configList);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laboratoryId]);

  const handleToggleEnabled = async (e) => {
    const value = e.target.checked;
    setIsEnabled(value);
    try {
      await notificationService.updateSetting(laboratoryId, value);
    } catch (error) {
      console.error('Failed to update notification setting:', error);
      setIsEnabled(!value);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (config) => {
    setEditingId(config.id);
    setFormData({ event: config.event, role: config.role, message: config.message });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.event || !formData.role || !formData.message.trim()) {
      alert('Please choose an event, a role, and enter a message.');
      return;
    }

    try {
      setSubmitting(true);
      if (editingId) {
        await notificationService.updateConfig(editingId, { ...formData, isActive: true });
      } else {
        await notificationService.addConfig({ laboratoryId: parseInt(laboratoryId, 10), ...formData });
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save notification rule:', error);
      alert('Failed to save. Check console for details.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notification rule?')) return;
    try {
      await notificationService.deleteConfig(id);
      fetchData();
    } catch (error) {
      console.error('Failed to delete notification rule:', error);
    }
  };

  const eventLabel = (key) => metadata.events.find((e) => e.key === key)?.label || key;
  const selectedPlaceholders = metadata.events.find((e) => e.key === formData.event)?.placeholders || [];

  return (
    <div className="container-fluid Container2 notification-settings-container common-FontFamily">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="common-FontWeight m-0">Notification Settings</h5>
        <div className="d-flex align-items-center">
          <span className="common-Labels me-2">Notifications {isEnabled ? 'Enabled' : 'Disabled'} for this Lab</span>
          <Switch checked={isEnabled} onChange={handleToggleEnabled} color="primary" />
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="common-Labels">Configured Rules</span>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          className="common-button"
          onClick={openAddModal}
        >
          Add Rule
        </Button>
      </div>

      <div className="table-responsive common-box-shadow common-bg-white">
        <table className="table table-hover mb-0 common-FontSize-small align-middle">
          <thead>
            <tr>
              <th>Event</th>
              <th>Notify Role</th>
              <th>Message</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-3">Loading...</td></tr>
            ) : configs.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-3 text-muted">No notification rules configured yet.</td></tr>
            ) : (
              configs.map((config) => (
                <tr key={config.id}>
                  <td>{eventLabel(config.event)}</td>
                  <td>{config.role}</td>
                  <td>{config.message}</td>
                  <td>
                    <span className={`status-badge ${config.isActive ? 'verified' : 'rejected'}`}>
                      {config.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="text-end">
                    <IconButton size="small" onClick={() => openEditModal(config)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(config.id)}>
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showModal} onClose={() => setShowModal(false)} fullWidth maxWidth="sm" className="common-modal">
        <DialogTitle className="d-flex justify-content-between align-items-center">
          {editingId ? 'Edit Notification Rule' : 'Add Notification Rule'}
          <IconButton onClick={() => setShowModal(false)} className="common-cross-button">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <div className="mb-3">
              <label className="common-Labels d-block">Event <span className="common-Asterisk">*</span></label>
              <FormControl fullWidth size="small">
                <Select name="event" value={formData.event} onChange={handleChange} displayEmpty required>
                  <MenuItem value="" disabled>Select an event</MenuItem>
                  {metadata.events.map((ev) => (
                    <MenuItem key={ev.key} value={ev.key}>{ev.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="mb-3">
              <label className="common-Labels d-block">Notify Role <span className="common-Asterisk">*</span></label>
              <FormControl fullWidth size="small">
                <Select name="role" value={formData.role} onChange={handleChange} displayEmpty required>
                  <MenuItem value="" disabled>Select a role</MenuItem>
                  {metadata.roles.map((role) => (
                    <MenuItem key={role} value={role}>{role}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="mb-2">
              <label className="common-Labels d-block">Message <span className="common-Asterisk">*</span></label>
              <textarea
                className="form-control common-Input"
                name="message"
                rows={3}
                value={formData.message}
                onChange={handleChange}
                placeholder="e.g. Report printed for {PatientName}"
                required
              />
            </div>
            {selectedPlaceholders.length > 0 && (
              <div className="common-Font-10 text-muted">
                Available placeholders: {selectedPlaceholders.map((p) => `{${p}}`).join(', ')}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowModal(false)} disabled={submitting}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}
